// src/components/common/DualGroupChat.js - FULLY UPDATED VERSION with robust timezone fixes
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { styled } from '@mui/system';
import moment from 'moment';
import 'moment/locale/th';
import EventService from '../../services/eventService';

// Import timeUtils functions (with fallback if they don't exist)
let timeUtilsAvailable = false;
let formatChatTimestamp, formatAbsoluteTime, isRecentTimestamp, getCurrentTimestamp;


try {
  const timeUtils = require('../../utils/timeUtils');
  formatChatTimestamp = timeUtils.formatChatTimestamp;
  formatAbsoluteTime = timeUtils.formatAbsoluteTime;
  isRecentTimestamp = timeUtils.isRecentTimestamp;
  getCurrentTimestamp = timeUtils.getCurrentTimestamp;
  timeUtilsAvailable = true;
  console.log('✅ TimeUtils loaded successfully');
} catch (error) {
  console.warn('⚠️ TimeUtils not found, using local implementations');
  timeUtilsAvailable = false;
}

// Import auth context with fallback
let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (error) {
  console.warn('AuthContext not found, using fallback auth');
  useAuth = () => ({
    isAuthenticated: false,
    currentUser: { id: 1, name: 'Guest User' }
  });
}

// LOCAL ROBUST TIMESTAMP FUNCTIONS (as fallback and additional safety)
const robustFormatTimestamp = (timestamp, language = 'TH') => {
  if (!timestamp) return language === 'TH' ? 'เมื่อสักครู่' : 'just now';
  
  try {
    moment.locale(language === 'TH' ? 'th' : 'en');
    
    let momentTime = moment(timestamp);
    
    // Check if timestamp is in the future (common timezone issue)
    const diffMinutes = moment().diff(momentTime, 'minutes');
    
    console.log(`🕐 Timestamp analysis:`, {
      original: timestamp,
      parsed: momentTime.format(),
      diffMinutes,
      isInFuture: diffMinutes < 0
    });
    
    // If more than 10 minutes in the future, apply fixes
    if (diffMinutes < -10) {
      console.log('🔧 Applying timezone fixes...');
      
      // Fix 1: Subtract 7 hours (Thailand UTC+7 issue)
      const fix1 = moment(timestamp).subtract(7, 'hours');
      const diff1 = moment().diff(fix1, 'minutes');
      
      // Fix 2: Treat as UTC and convert to local
      const fix2 = moment.utc(timestamp).local();
      const diff2 = moment().diff(fix2, 'minutes');
      
      // Fix 3: Parse as local time but subtract offset
      const fix3 = moment(timestamp).utcOffset('+07:00');
      const diff3 = moment().diff(fix3, 'minutes');
      
      console.log('🔧 Fix attempts:', {
        'subtract7h': { time: fix1.format(), diff: diff1, valid: diff1 >= 0 && diff1 < 1440 },
        'utcToLocal': { time: fix2.format(), diff: diff2, valid: diff2 >= 0 && diff2 < 1440 },
        'utcOffset': { time: fix3.format(), diff: diff3, valid: diff3 >= 0 && diff3 < 1440 }
      });
      
      // Choose the best fix
      if (diff1 >= 0 && diff1 < 1440) { // Within last 24 hours
        momentTime = fix1;
        console.log('✅ Applied subtract 7 hours fix');
      } else if (diff2 >= 0 && diff2 < 1440) {
        momentTime = fix2;
        console.log('✅ Applied UTC to local fix');
      } else if (diff3 >= 0 && diff3 < 1440) {
        momentTime = fix3;
        console.log('✅ Applied UTC offset fix');
      } else {
        // If all fixes fail, use current time
        momentTime = moment();
        console.log('⚠️ All fixes failed, using current time');
      }
    }
    
    const result = momentTime.fromNow();
    
    // Final safety check: if it still shows future time, force to "just now"
    if (result.includes('อีก') || result.includes('in ')) {
      console.log('🚨 Still showing future time, forcing to "just now"');
      return language === 'TH' ? 'เมื่อสักครู่' : 'just now';
    }
    
    console.log(`✅ Final timestamp result: "${result}"`);
    return result;
    
  } catch (error) {
    console.error('❌ Error in robustFormatTimestamp:', error);
    return language === 'TH' ? 'เมื่อสักครู่' : 'just now';
  }
};

const robustFormatAbsoluteTime = (timestamp, language = 'TH') => {
  if (!timestamp) return '';
  
  try {
    moment.locale(language === 'TH' ? 'th' : 'en');
    
    let momentTime = moment(timestamp);
    const diffMinutes = moment().diff(momentTime, 'minutes');
    
    // Apply same timezone fixes as above
    if (diffMinutes < -10) {
      const fix1 = moment(timestamp).subtract(7, 'hours');
      const diff1 = moment().diff(fix1, 'minutes');
      
      if (diff1 >= 0 && diff1 < 1440) {
        momentTime = fix1;
      } else {
        const fix2 = moment.utc(timestamp).local();
        const diff2 = moment().diff(fix2, 'minutes');
        if (diff2 >= 0 && diff2 < 1440) {
          momentTime = fix2;
        } else {
          momentTime = moment();
        }
      }
    }
    
    const now = moment();
    const diffDays = now.diff(momentTime, 'days');
    
    if (diffDays === 0) {
      return momentTime.format('HH:mm');
    } else if (diffDays === 1) {
      return language === 'TH' ? `เมื่อวาน ${momentTime.format('HH:mm')}` : `Yesterday ${momentTime.format('HH:mm')}`;
    } else if (diffDays < 7) {
      return momentTime.format('ddd HH:mm');
    } else {
      return momentTime.format('DD/MM/YY HH:mm');
    }
    
  } catch (error) {
    console.error('Error formatting absolute time:', error);
    return '';
  }
};

const robustIsRecentTimestamp = (timestamp) => {
  if (!timestamp) return false;
  
  try {
    let momentTime = moment(timestamp);
    const diffMinutes = moment().diff(momentTime, 'minutes');
    
    // Apply timezone fixes if needed
    if (diffMinutes < -10) {
      const fix1 = moment(timestamp).subtract(7, 'hours');
      const diff1 = moment().diff(fix1, 'minutes');
      
      if (diff1 >= 0 && diff1 < 1440) {
        momentTime = fix1;
      } else {
        const fix2 = moment.utc(timestamp).local();
        momentTime = fix2;
      }
    }
    
    const diffSeconds = moment().diff(momentTime, 'seconds');
    return diffSeconds >= 0 && diffSeconds < 60;
    
  } catch (error) {
    return false;
  }
};

const robustGetCurrentTimestamp = () => {
  return new Date().toISOString();
};

// Use timeUtils if available, otherwise use local robust functions
const safeFormatChatTimestamp = timeUtilsAvailable ? 
  (timestamp, language) => {
    try {
      const result = formatChatTimestamp(timestamp, language);
      // Double-check: if timeUtils still returns future time, use robust function
      if (result.includes('อีก') || result.includes('in ')) {
        console.log('⚠️ TimeUtils returned future time, using robust fallback');
        return robustFormatTimestamp(timestamp, language);
      }
      return result;
    } catch (error) {
      return robustFormatTimestamp(timestamp, language);
    }
  } : robustFormatTimestamp;

const safeFormatAbsoluteTime = timeUtilsAvailable ? 
  (timestamp, language) => {
    try {
      return formatAbsoluteTime(timestamp, language);
    } catch (error) {
      return robustFormatAbsoluteTime(timestamp, language);
    }
  } : robustFormatAbsoluteTime;

const safeIsRecentTimestamp = timeUtilsAvailable ? 
  (timestamp) => {
    try {
      return isRecentTimestamp(timestamp);
    } catch (error) {
      return robustIsRecentTimestamp(timestamp);
    }
  } : robustIsRecentTimestamp;

const safeGetCurrentTimestamp = timeUtilsAvailable ? getCurrentTimestamp : robustGetCurrentTimestamp;

// Styled Components (keeping your existing styles)
const ChatContainer = styled(Paper)(({ theme, hasMessages }) => ({
  height: hasMessages ? '450px' : '220px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
}));

const ChatHeader = styled(Box)(({ theme, chatType }) => ({
  padding: '16px 20px',
  backgroundColor: chatType === 'interested' ? '#e8f5fd' : '#e8f5e8',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const ChatTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
}));

const MessagesContainer = styled(Box)(({ theme, hasMessages }) => ({
  flex: 1,
 /* ไม่มีข้อความ → ซ่อน scrollbar / มีข้อความ → auto ตามเดิม */
  overflow: hasMessages ? 'auto' : 'hidden',
  backgroundColor: '#fafafa',
  padding: '12px',

  /* กำหนดสไตล์ scrollbar เฉพาะตอนที่ต้องแสดงเท่านั้น  */
  ...(hasMessages && {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
      borderRadius: '3px',
    },
  }),
}));

const MessageInputContainer = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: '#ffffff',
  borderTop: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const MessageBubble = styled(Box)(({ theme, isOwnMessage, isRecent }) => ({
  display: 'flex',
  gap: '12px',
  marginBottom: '16px',
  alignItems: 'flex-start',
  animation: isRecent ? 'slideInFromBottom 0.3s ease-out' : 'none',
  '@keyframes slideInFromBottom': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const MessageContent = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '100%',
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: '#1a1a1a',
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#888',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#3a7bd5',
  },
}));

const MessageText = styled(Typography)(({ theme, isRecent }) => ({
  fontSize: '0.95rem',
  lineHeight: 1.4,
  color: '#333',
  wordBreak: 'break-word',
  backgroundColor: isRecent ? '#f0f8ff' : '#ffffff',
  padding: '8px 12px',
  borderRadius: '18px',
  border: isRecent ? '1px solid #3a7bd5' : '1px solid #e0e0e0',
  transition: 'all 0.3s ease',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: '36px',
  height: '36px',
  fontSize: '0.9rem',
  fontWeight: 'bold',
}));

const RecentIndicator = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: '#4caf50',
  marginLeft: '4px',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

const DualGroupChat = ({ 
  eventId, 
  chatType, 
  title, 
  isAuthenticated, 
  getAuthHeaders,
  userGroups 
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [showAbsoluteTime, setShowAbsoluteTime] = useState({});
  const messagesEndRef = useRef(null);

  // Get real user from auth context
  const { currentUser } = useAuth();
  const realCurrentUser = currentUser || { id: 1, name: 'Guest User' };

  // Auto-refresh timestamps every minute
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update relative timestamps
      setMessages(prev => [...prev]);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load messages from database
  const loadMessages = useCallback(async () => {
    if (!eventId || !isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`🔄 [DualGroupChat] Loading messages for event ${eventId}, chat ${chatType}...`);
      
      const data = await EventService.getGroupChatMessages(
        eventId, 
        chatType, 
        getAuthHeaders,
        50, 
        0
      );
      
      // Enhanced debugging with timestamp analysis
      if (data.messages && data.messages.length > 0) {
        console.log(`🕐 [DualGroupChat] Analyzing ${data.messages.length} timestamps:`, 
          data.messages.map(m => {
            const now = moment();
            const msgTime = moment(m.created_at);
            const diffMinutes = now.diff(msgTime, 'minutes');
            
            return {
              id: m.id, 
              raw_timestamp: m.created_at,
              parsed_time: msgTime.format(),
              diff_minutes: diffMinutes,
              is_future: diffMinutes < 0,
              relative_safe: safeFormatChatTimestamp(m.created_at, 'TH'),
              is_recent: safeIsRecentTimestamp(m.created_at)
            };
          })
        );
      }
      
      setMessages(data.messages || []);
      setConnectionStatus(data.demo ? 'demo' : 'connected');
      
      console.log(`✅ [DualGroupChat] Loaded ${data.messages?.length || 0} messages from database for ${chatType} chat`);
      
    } catch (err) {
      console.error('❌ [DualGroupChat] Error loading messages from database:', err);
      setConnectionStatus('error');
      setMessages([]);
      setError('Unable to load messages from database');
    } finally {
      setLoading(false);
    }
  }, [eventId, isAuthenticated, chatType, getAuthHeaders]);

  // Load messages on mount
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send message to database
  const sendMessage = async () => {
    if (!newMessage.trim() || !isAuthenticated || sending) return;

    setSending(true);
    const messageToSend = newMessage.trim();
    setNewMessage('');

    try {
      console.log(`📤 [DualGroupChat] Sending message to database for ${chatType} chat...`);
      
      const response = await EventService.sendGroupChatMessage(
        eventId,
        chatType,
        messageToSend,
        getAuthHeaders
      );
      
      // Add to local messages immediately with safe timestamp
      const newMessageData = {
        ...response.data,
        user_name: realCurrentUser.name,
        user_id: realCurrentUser.id,
        // ALWAYS use current time for new messages to avoid timezone issues
        created_at: safeGetCurrentTimestamp()
      };
      
      console.log(`📝 Adding new message with timestamp:`, {
        original_response: response.data.created_at,
        safe_timestamp: newMessageData.created_at,
        relative_display: safeFormatChatTimestamp(newMessageData.created_at, 'TH')
      });
      
      setMessages(prev => [...prev, newMessageData]);
      
      if (response.demo) {
        console.log(`⚠️ [DualGroupChat] Message sent in demo mode for ${chatType} chat`);
      } else {
        console.log(`✅ [DualGroupChat] Message sent to database for ${chatType} chat`);
      }
      
    } catch (err) {
      console.error('❌ [DualGroupChat] Error sending message to database:', err);
      
      // Add message locally as fallback with safe timestamp
      const fallbackMessage = {
        id: Date.now(),
        user_id: realCurrentUser.id,
        user_name: realCurrentUser.name,
        message: messageToSend,
        created_at: safeGetCurrentTimestamp(),
        avatar_url: null
      };
      
      setMessages(prev => [...prev, fallbackMessage]);
      console.log(`⚠️ [DualGroupChat] Message added locally with safe timestamp`);
      
    } finally {
      setSending(false);
    }
  };

  // Handle enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Toggle between relative and absolute time display
  const toggleTimeDisplay = (messageId) => {
    setShowAbsoluteTime(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Get connection status indicator
  const getConnectionIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return { color: '#4caf50', text: 'เชื่อมต่อฐานข้อมูล' };
      case 'demo':
        return { color: '#ff9800', text: 'โหมดทดสอบ' };
      case 'error':
        return { color: '#f44336', text: 'ไม่สามารถเชื่อมต่อ' };
      default:
        return { color: '#9e9e9e', text: 'กำลังตรวจสอบ' };
    }
  };
  
  const hasMessages = messages.length > 0;
  return (
    <ChatContainer>
      {/* Chat Header with connection status */}
      <ChatHeader hasMessages={hasMessages} chatType={chatType}>
        <GroupIcon sx={{ color: chatType === 'interested' ? '#3a7bd5' : '#4caf50' }} />
        <ChatTitle>{title}</ChatTitle>
        
        {/* Connection status indicator */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getConnectionIndicator().color,
            }}
          />
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666',
              fontSize: '0.7rem',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {getConnectionIndicator().text}
          </Typography>
        </Box>
      </ChatHeader>

      {/* Messages Container */}
      <MessagesContainer hasMessages={hasMessages}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            {error}
            <Typography variant="body2" sx={{ mt: 1 }}>
              หากปัญหานี้ยังคงอยู่ กรุณารีเฟรชหน้าเว็บ
            </Typography>
          </Alert>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, color: '#888' }}>
            <Typography>ยังไม่มีข้อความในกลุ่ม</Typography>
            <Typography variant="body2">เป็นคนแรกที่เริ่มการสนทนา!</Typography>
          </Box>
        ) : (
          <>
            {messages.map((message) => {
              const isOwnMessage = message.user_id === realCurrentUser.id;
              const showAbsolute = showAbsoluteTime[message.id];
              const isRecent = safeIsRecentTimestamp(message.created_at);
              
              return (
                <MessageBubble 
                  key={message.id} 
                  isOwnMessage={isOwnMessage}
                  isRecent={isRecent}
                >
                  <UserAvatar
                    src={message.avatar_url}
                    sx={{ 
                      bgcolor: isOwnMessage ? '#3a7bd5' : '#e0e0e0',
                      color: isOwnMessage ? 'white' : '#666'
                    }}
                  >
                    {getUserInitials(message.user_name)}
                  </UserAvatar>
                  
                  <MessageContent>
                    <MessageHeader>
                      <UserName>{message.user_name}</UserName>
                      <MessageTime 
                        onClick={() => toggleTimeDisplay(message.id)}
                        title={`Click to ${showAbsolute ? 'show relative' : 'show absolute'} time`}
                      >
                        {showAbsolute 
                          ? safeFormatAbsoluteTime(message.created_at, 'TH')
                          : safeFormatChatTimestamp(message.created_at, 'TH')
                        }
                      </MessageTime>
                      {isRecent && <RecentIndicator />}
                    </MessageHeader>
                    
                    <MessageText isRecent={isRecent}>
                      {message.message}
                    </MessageText>
                  </MessageContent>
                </MessageBubble>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      {/* Message Input */}
      <MessageInputContainer>
        {isAuthenticated ? (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="เขียนความคิดเห็น..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                backgroundColor: '#f8f9fa',
                '& fieldset': {
                  border: '1px solid #e0e0e0',
                },
                '&:hover fieldset': {
                  border: '1px solid #3a7bd5',
                },
                '&.Mui-focused fieldset': {
                  border: '1px solid #3a7bd5',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    sx={{
                      color: newMessage.trim() ? '#3a7bd5' : '#ccc',
                      p: 1,
                    }}
                  >
                    {sending ? <CircularProgress size={20} /> : <SendIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            กรุณาเข้าสู่ระบบเพื่อเข้าร่วมการสนทนา
          </Alert>
        )}
      </MessageInputContainer>
    </ChatContainer>
  );
};

// Debug functions for console testing
const debugTimestamp = (timestamp) => {
  console.log('🔍 Timestamp Debug for:', timestamp);
  console.log('Current time:', moment().format());
  console.log('Parsed time:', moment(timestamp).format());
  console.log('Diff minutes:', moment().diff(moment(timestamp), 'minutes'));
  console.log('Robust result:', robustFormatTimestamp(timestamp, 'TH'));
  console.log('Is recent:', robustIsRecentTimestamp(timestamp));
};

// Export debug function to window for console testing
if (typeof window !== 'undefined') {
  window.debugTimestamp = debugTimestamp;
  window.robustFormatTimestamp = robustFormatTimestamp;
  window.testCurrentTime = () => {
    const now = safeGetCurrentTimestamp();
    console.log('Current timestamp:', now);
    console.log('Formatted result:', safeFormatChatTimestamp(now, 'TH'));
  };
}

export default DualGroupChat;